import { create } from 'zustand'

export interface Job {
  job_id: string
  status: string
  content_prompt: string
  output_quantity: number
  media_mode: string
  total_results: number
  completed_results: number
  failed_results: number
  approved_count?: number
  credits_charged: number
  title?: string
  notes?: string
  bookmarked?: boolean
  // Full job config for details view
  filter_prompt?: string
  publish_mode?: string
  schedule_prompt?: string
  instruction_prompt?: string
  on_job_finish_webhook?: string
  on_job_finish_email?: string
  on_schedule_webhook_default?: string
  on_schedule_email_default?: string
  tracer?: string
  inbox_tracer?: string
  nocodb_results_view_url?: string
  created_at: string
  updated_at: string
  completed_at?: string
}

export interface Result {
  result_id: string
  job_id: string
  status: string
  media_type: 'image' | 'video'
  media_url: string
  thumbnail_url?: string
  caption: string
  hashtags: string[]
  ai_notes?: string
  match_score?: number
  approved: boolean
  user_notes?: string
  scheduled_datetime?: string
  on_schedule_webhook?: string
  on_schedule_email?: string
  instruction_text?: string
  tracer?: string
  inbox_tracer?: string
  bookmarked?: boolean
  created_at: string
  updated_at: string
}

export interface Scheduled {
  scheduled_id: string
  result_id: string
  job_id: string
  scheduled_datetime: string
  media_type: 'image' | 'video'
  media_url: string
  thumbnail_url?: string
  caption: string
  hashtags: string[]
  instruction_text?: string
  fired: boolean
  fired_at?: string
  proof_url?: string
  proof_timestamp?: string
  tracer?: string
  inbox_tracer?: string
  created_at: string
}

interface JobsState {
  jobs: Job[]
  currentJob: Job | null
  results: Result[]
  scheduled: Scheduled[]
  loading: boolean
  error: string | null
  setJobs: (jobs: Job[]) => void
  setCurrentJob: (job: Job | null) => void
  setResults: (results: Result[]) => void
  setScheduled: (scheduled: Scheduled[]) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  updateJob: (jobId: string, updates: Partial<Job>) => void
  updateResult: (resultId: string, updates: Partial<Result>) => void
}

export const useJobsStore = create<JobsState>((set) => ({
  jobs: [],
  currentJob: null,
  results: [],
  scheduled: [],
  loading: false,
  error: null,

  setJobs: (jobs) => set({ jobs }),
  setCurrentJob: (job) => set({ currentJob: job }),
  setResults: (results) => set({ results }),
  setScheduled: (scheduled) => set({ scheduled }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  updateJob: (jobId, updates) => set((state) => ({
    jobs: state.jobs.map((j) =>
      j.job_id === jobId ? { ...j, ...updates } : j
    ),
    currentJob: state.currentJob?.job_id === jobId
      ? { ...state.currentJob, ...updates }
      : state.currentJob,
  })),

  updateResult: (resultId, updates) => set((state) => ({
    results: state.results.map((r) =>
      r.result_id === resultId ? { ...r, ...updates } : r
    ),
  })),
}))
